/**
 * WF Delivery Countdown — always-ticking announcement bar timer.
 *
 * Synced to live delivery config from planners API.
 * All time calculations use Asia/Singapore (SGT).
 *
 * Always shows a live countdown to the next delivery cutoff, filtered by
 * blackout dates, override days, and public slot availability.
 *
 * Static SEO fallback visible until JS hydrates.
 */
(function () {
  'use strict';

  var CACHE_KEY = 'wf_dc_config';
  var CACHE_TTL = 5 * 60 * 1000;
  var AVAILABILITY_TTL = 60 * 1000;
  var URGENT_MS = 3 * 60 * 60 * 1000;
  var REFRESH_MS = 60 * 1000;
  var API_BASE = 'https://planners.dand3.com';
  var STORE_KEY = 'WF';

  var SGT = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Singapore',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });

  function sgtParts(now) {
    var parts = SGT.formatToParts(now || new Date());
    var o = {};
    for (var i = 0; i < parts.length; i++) {
      o[parts[i].type] = Number(parts[i].value) || 0;
    }
    return o;
  }

  function sgtDateStr(now) {
    var p = sgtParts(now);
    return String(p.year).padStart(4, '0') + '-' +
           String(p.month).padStart(2, '0') + '-' +
           String(p.day).padStart(2, '0');
  }

  function dow(dateStr) {
    return new Date(dateStr + 'T12:00:00+08:00').getUTCDay();
  }

  function addDays(dateStr, n) {
    var d = new Date(dateStr + 'T12:00:00+08:00');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  }

  function sgtEpoch(dateStr, hour, minute) {
    var parts = dateStr.split('-').map(Number);
    return Date.UTC(parts[0], parts[1] - 1, parts[2], (hour || 0) - 8, minute || 0, 0);
  }

  function fmtTime(h, m) {
    var hr = h % 12 || 12;
    var ap = h < 12 ? 'am' : 'pm';
    return m === 0 ? hr + ap : hr + ':' + String(m).padStart(2, '0') + ap;
  }

  function slotWindow(s) {
    return fmtTime(s.startHour || 0, s.startMinute || 0) + '–' +
           fmtTime(s.endHour || 0, s.endMinute || 0);
  }

  function fmtCountdown(ms) {
    if (ms <= 0) return '0s';
    var t = Math.floor(ms / 1000);
    var h = Math.floor(t / 3600);
    var m = Math.floor((t % 3600) / 60);
    var s = t % 60;
    if (h > 0) return h + 'h ' + m + 'm ' + s + 's';
    if (m > 0) return m + 'm ' + s + 's';
    return s + 's';
  }

  function isBlackout(cfg, d) {
    var list = cfg.blackout_dates || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].date === d) return true;
    }
    return false;
  }

  function getOverride(cfg, d) {
    var list = cfg.override_days || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].date === d) return list[i];
    }
    return null;
  }

  function allSlotsForDate(cfg, dateStr) {
    if (isBlackout(cfg, dateStr)) return [];
    var ov = getOverride(cfg, dateStr);
    if (ov && ov.delivery && ov.delivery.slots) return ov.delivery.slots;
    var d = dow(dateStr);
    return (cfg.timeslots || []).filter(function (s) {
      return s.availableDays && s.availableDays.indexOf(d) >= 0;
    });
  }

  function availabilityMap(availability) {
    var map = {};
    var list = availability && Array.isArray(availability.slots) ? availability.slots : null;
    if (!list) return null;
    for (var i = 0; i < list.length; i++) {
      map[list[i].slotId] = list[i].available === true && list[i].visible !== false;
    }
    return map;
  }

  function filterAvailableSlots(slots, availability) {
    if (availability && availability.blackout) return [];
    var map = availabilityMap(availability);
    if (!map) return slots;
    return slots.filter(function (slot) { return map[slot.id] === true; });
  }

  function slotsBeforeCutoff(cfg, dateStr, availability, now) {
    var slots = allSlotsForDate(cfg, dateStr);
    var nowEpoch = (now || new Date()).getTime();
    return filterAvailableSlots(slots, availability).filter(function (s) {
      return nowEpoch < sgtEpoch(dateStr, s.cutoffHour || 0, s.cutoffMinute || 0);
    });
  }

  function buildStateFromSlots(dateStr, dayOffset, openSlots, current) {
    var nowEpoch = current.getTime();
    var slot = openSlots[0];
    var cutoffAt = sgtEpoch(dateStr, slot.cutoffHour || 0, slot.cutoffMinute || 0);
    var label = 'same-day';
    if (dayOffset === 1) {
      label = 'tomorrow';
    } else if (dayOffset > 1) {
      label = new Date(dateStr + 'T12:00:00+08:00')
        .toLocaleDateString('en-SG', { weekday: 'long', timeZone: 'Asia/Singapore' });
    }

    var windows = [];
    if (dayOffset === 0) {
      for (var w = 0; w < openSlots.length; w++) windows.push(slotWindow(openSlots[w]));
    }

    return {
      remaining: cutoffAt - nowEpoch,
      cutoffAt: cutoffAt,
      urgent: cutoffAt - nowEpoch <= URGENT_MS,
      cutoff: fmtTime(slot.cutoffHour || 0, slot.cutoffMinute || 0),
      window: slotWindow(slot),
      openWindows: windows,
      dayLabel: label,
      date: dateStr,
    };
  }

  /**
   * Find the next available slot + compute ms remaining until its cutoff.
   * Searches today first, then up to 7 days ahead (handles consecutive blackouts).
   */
  function computeState(cfg, availabilityByDate, now) {
    var current = now || new Date();
    var today = sgtDateStr(current);

    for (var i = 0; i <= 7; i++) {
      var dateStr = addDays(today, i);
      var openSlots = slotsBeforeCutoff(cfg, dateStr, availabilityByDate && availabilityByDate[dateStr], current);
      if (openSlots.length === 0) continue;
      return buildStateFromSlots(dateStr, i, openSlots, current);
    }

    return { noSlots: true };
  }

  function render(el, state) {
    var live = el.querySelector('.wf-dc__live');
    var stat = el.querySelector('.wf-dc__static');
    if (!live || !stat) return;

    stat.style.display = 'none';
    live.style.display = '';

    if (!state || state.noSlots) {
      live.innerHTML = '<span class="wf-dc__icon">\uD83D\uDE9A</span> ' +
        'Free same-day flower delivery across Singapore';
      return;
    }

    var icon = state.urgent ? '\u23F0' : '\uD83D\uDE9A';
    var remaining = state.cutoffAt ? state.cutoffAt - Date.now() : state.remaining;
    var time = '<strong>' + fmtCountdown(remaining) + '</strong>';

    if (state.dayLabel === 'same-day' && state.urgent) {
      var suffix = '';
      if (state.openWindows && state.openWindows.length > 0) {
        suffix = ' \u2014 today\u2019s slots: ' + state.openWindows.join(', ');
      }
      live.innerHTML = '<span class="wf-dc__icon">' + icon + '</span> ' +
        time + ' left for same-day delivery' + suffix;
    } else {
      live.innerHTML = '<span class="wf-dc__icon">' + icon + '</span> ' +
        time + ' \u2014 order by <strong>' + state.cutoff + '</strong> for ' +
        state.dayLabel + ' ' + state.window + ' delivery';
    }
  }

  function loadConfig(cb) {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        var c = JSON.parse(raw);
        if (c._ts && Date.now() - c._ts < CACHE_TTL) return cb(c.data);
      }
    } catch (e) { /* ignore */ }

    fetch(API_BASE + '/api/delivery/' + STORE_KEY + '/public?_t=' + Date.now(), {
      credentials: 'omit', cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify({ _ts: Date.now(), data: data }));
        } catch (e) { /* quota */ }
        cb(data);
      })
      .catch(function () { /* keep static fallback */ });
  }

  function loadAvailability(dateStr) {
    var key = 'wf_dc_availability_' + dateStr;
    try {
      var raw = sessionStorage.getItem(key);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached._ts && Date.now() - cached._ts < AVAILABILITY_TTL) return Promise.resolve(cached.data);
      }
    } catch (e) { /* ignore */ }

    return fetch(API_BASE + '/api/delivery/' + STORE_KEY + '/availability?date=' + encodeURIComponent(dateStr) + '&_t=' + Date.now(), {
      credentials: 'omit', cache: 'no-store',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) {
          try {
            sessionStorage.setItem(key, JSON.stringify({ _ts: Date.now(), data: data }));
          } catch (e) { /* quota */ }
        }
        return data;
      })
      .catch(function () { return null; });
  }

  function findStateWithAvailability(cfg, index, availabilityByDate, now) {
    var today = sgtDateStr(now);
    if (index > 7) return Promise.resolve(computeState(cfg, availabilityByDate, now));

    var dateStr = addDays(today, index);
    if (allSlotsForDate(cfg, dateStr).length === 0) {
      return findStateWithAvailability(cfg, index + 1, availabilityByDate, now);
    }

    return loadAvailability(dateStr).then(function (availability) {
      availabilityByDate[dateStr] = availability;
      var current = now || new Date();
      var openSlots = slotsBeforeCutoff(cfg, dateStr, availability, current);
      if (openSlots.length > 0) {
        return buildStateFromSlots(dateStr, index, openSlots, current);
      }
      return findStateWithAvailability(cfg, index + 1, availabilityByDate, now);
    });
  }

  function init() {
    var el = document.getElementById('wf-delivery-countdown');
    if (!el) return;

    loadConfig(function (cfg) {
      var latestState = null;
      var refreshInFlight = false;

      function refreshState() {
        if (refreshInFlight) return;
        refreshInFlight = true;
        findStateWithAvailability(cfg, 0, {})
          .then(function (state) {
            latestState = state;
            render(el, latestState);
          })
          .finally(function () {
            refreshInFlight = false;
          });
      }

      function tick() {
        if (!latestState || (latestState.cutoffAt && Date.now() >= latestState.cutoffAt)) {
          refreshState();
          return;
        }
        render(el, latestState);
      }

      refreshState();
      setInterval(tick, 1000);
      setInterval(refreshState, REFRESH_MS);
    });
  }

  window.WF_DELIVERY_COUNTDOWN = {
    _test: {
      computeState: computeState,
      render: render,
      filterAvailableSlots: filterAvailableSlots,
      findStateWithAvailability: findStateWithAvailability,
      sgtEpoch: sgtEpoch,
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
